/**
 * Environment-based Configuration Manager
 * Best practice for React applications - uses build-time environment variables
 * No AWS credentials needed, fast loading, secure
 */

// Helper function to parse boolean values
const parseBoolean = (value) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
};

// Helper function to parse integer values
const parseInteger = (value, defaultValue = 0) => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Helper function to parse float values
const parseFloatValue = (value, defaultValue = 0.0) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Application Configuration
 * All values are loaded from environment variables at build time
 */
const config = {
  // API Configuration - Glucose REST API
  api: {
    endpoint: process.env.REACT_APP_API_ENDPOINT || 'https://982mlzt6x6.execute-api.ap-southeast-2.amazonaws.com/prod',
    region: process.env.REACT_APP_API_REGION || 'ap-southeast-2',
    timeout: parseInteger(process.env.REACT_APP_API_TIMEOUT, 60000),
    retryCount: parseInteger(process.env.REACT_APP_API_RETRY_COUNT, 2),
    retryDelay: parseInteger(process.env.REACT_APP_API_RETRY_DELAY, 2000)
  },

  // AWS Cognito Configuration - Glucose User Pool
  cognito: {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'ap-southeast-2_Jwc8loRjl',
    userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID || 'cbk37cbntvgl37k0m7b9jhdh4',
    domain: process.env.REACT_APP_COGNITO_DOMAIN || 'glucose.auth.ap-southeast-2.amazoncognito.com',
    region: process.env.REACT_APP_COGNITO_REGION || 'ap-southeast-2'
  },

  // Bedrock Agent Configuration - Glucose Agent
  bedrock: {
    agentId: process.env.REACT_APP_BEDROCK_AGENT_ID || 'UHWBEBJVBH',
    agentAlias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'W5OWNQP70R',
    modelId: process.env.REACT_APP_BEDROCK_MODEL_ID || 'anthropic.claude-3-sonnet-20240229-v1:0',
    maxTokens: parseInteger(process.env.REACT_APP_BEDROCK_MAX_TOKENS, 4096),
    temperature: parseFloatValue(process.env.REACT_APP_BEDROCK_TEMPERATURE, 0.7)
  },

  // Feature Flags
  features: {
    enableFeedback: parseBoolean(process.env.REACT_APP_ENABLE_FEEDBACK ?? 'true'),
    enableFileUpload: parseBoolean(process.env.REACT_APP_ENABLE_FILE_UPLOAD ?? 'false'),
    enableSessionRating: parseBoolean(process.env.REACT_APP_ENABLE_SESSION_RATING ?? 'true'),
    enableBugReporting: parseBoolean(process.env.REACT_APP_ENABLE_BUG_REPORTING ?? 'true')
  },

  // Monitoring Configuration
  monitoring: {
    enableXRay: parseBoolean(process.env.REACT_APP_ENABLE_XRAY ?? 'true'),
    logLevel: process.env.REACT_APP_LOG_LEVEL || 'info',
    enableRequestTracking: parseBoolean(process.env.REACT_APP_ENABLE_REQUEST_TRACKING ?? 'true')
  },

  // Chat Configuration
  chat: {
    maxMessageLength: parseInteger(process.env.REACT_APP_MAX_MESSAGE_LENGTH, 4000),
    minMessageLength: parseInteger(process.env.REACT_APP_MIN_MESSAGE_LENGTH, 2),
    rateLimitDelay: parseInteger(process.env.REACT_APP_RATE_LIMIT_DELAY, 1000),
    maxSessionIdLength: parseInteger(process.env.REACT_APP_MAX_SESSION_ID_LENGTH, 100)
  },

  // Theme Configuration - Diabetes Australia Style Guide
  theme: {
    primary: process.env.REACT_APP_THEME_PRIMARY || '#005EB8',
    secondary: process.env.REACT_APP_THEME_SECONDARY || '#00A9CE',
    accent: process.env.REACT_APP_THEME_ACCENT || '#F37021',
    success: process.env.REACT_APP_THEME_SUCCESS || '#00843D',
    background: process.env.REACT_APP_THEME_BACKGROUND || '#F5F7FA',
    textPrimary: process.env.REACT_APP_THEME_TEXT_PRIMARY || '#333333',
    textSecondary: process.env.REACT_APP_THEME_TEXT_SECONDARY || '#666666',
    fontFamily: process.env.REACT_APP_THEME_FONT_FAMILY || "'Open Sans', sans-serif"
  }
};

/**
 * Configuration validation
 * Ensures all required values are present
 */
const validateConfig = () => {
  const errors = [];

  // Validate required API configuration
  if (!config.api.endpoint) {
    errors.push('API endpoint is required');
  }

  // Validate required Cognito configuration
  if (!config.cognito.userPoolId) {
    errors.push('Cognito User Pool ID is required');
  }
  if (!config.cognito.userPoolClientId) {
    errors.push('Cognito User Pool Client ID is required');
  }

  // Validate required Bedrock configuration
  if (!config.bedrock.agentId) {
    errors.push('Bedrock Agent ID is required');
  }
  if (!config.bedrock.agentAlias) {
    errors.push('Bedrock Agent Alias is required');
  }

  if (errors.length > 0) {
    console.error('❌ Configuration validation failed:', errors);
    throw new Error(`Configuration validation failed: ${errors.join(', ')}`);
  }

  return true;
};

/**
 * Initialize configuration
 * Validates and logs configuration status
 */
const initializeConfig = () => {
  try {
    validateConfig();
    return config;
  } catch (error) {
    console.error('❌ Configuration initialization failed:', error);
    throw error;
  }
};

// Export configuration sections
export const getApiConfig = () => config.api;
export const getCognitoConfig = () => config.cognito;
export const getBedrockConfig = () => config.bedrock;
export const getFeatureFlags = () => config.features;
export const getMonitoringConfig = () => config.monitoring;
export const getChatConfig = () => config.chat;
export const getThemeConfig = () => config.theme;

// Export full configuration
export const getConfig = () => config;

// Export initialization function
export { initializeConfig };

// Default export
export default {
  config,
  initializeConfig,
  getApiConfig,
  getCognitoConfig,
  getBedrockConfig,
  getFeatureFlags,
  getMonitoringConfig,
  getChatConfig,
  getThemeConfig,
  getConfig
}; 